<script setup>
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useUserGroupsStore, useUsersStore } from '@/stores'
import axios from '@axios'

const route = useRoute()
let thePublicId = Array.isArray(route.params.all) ? route.params.all[0] : route.params.all
thePublicId = thePublicId?.toLowerCase()

const token = ref(route.query.token || '')

const usersStore = useUsersStore()
const { loginDialogVerificationType, loginDialogMode, user } = storeToRefs(usersStore)

const userGroupsStore = useUserGroupsStore()
const { guestUserGroup } = storeToRefs(userGroupsStore)
userGroupsStore.getGuestUserGroup(thePublicId, 'spouse')

const invitation = ref(null)
const acceptState = ref('idle') // idle | working | accepted | declined | error
const errorMessage = ref('')

async function loadInvitation() {
  if (!token.value) return
  try {
    const { data } = await axios.get(`/user/personal-admins/invitation/${encodeURIComponent(token.value)}`)
    invitation.value = data.invitation

    // Pass token + adminEmail through to loginJoinDialog (read by the
    // personal-admin registration branch). Without this, the dialog has no
    // way to pre-fill the locked email or include the token in the POST.
    usersStore.personalAdminInvitation = { token: token.value, ...data.invitation }
  } catch (err) {
    errorMessage.value = err?.response?.data?.error || 'This invitation is no longer valid.'
  }
}

async function accept() {
  acceptState.value = 'working'
  try {
    await axios.post('/user/personal-admins/accept-by-token', { token: token.value })
    acceptState.value = 'accepted'
  } catch (err) {
    acceptState.value = 'error'
    errorMessage.value = err?.response?.data?.error || 'Could not accept invitation.'
  }
}

async function decline() {
  acceptState.value = 'working'
  try {
    await axios.post('/user/personal-admins/decline-by-token', { token: token.value })
    acceptState.value = 'declined'
  } catch (err) {
    acceptState.value = 'error'
    errorMessage.value = err?.response?.data?.error || 'Could not decline invitation.'
  }
}

onMounted(async () => {
  loginDialogVerificationType.value = 'personal-admin'
  await loadInvitation()
})

watch(loginDialogMode, newMode => {
  if (newMode) loginDialogVerificationType.value = 'personal-admin'
})
</script>

<template>
  <div v-if="guestUserGroup">
    <section class="home-masthead">
      <div class="content-wrapper">
        <div
          v-if="invitation && user && user.email && invitation.adminEmail && user.email.toLowerCase() === invitation.adminEmail.toLowerCase()"
          class="invitation-card"
        >
          <h2>You&rsquo;ve been invited to manage {{ invitation.memberFirstName }}&rsquo;s RM account</h2>
          <p>
            As an admin account you&rsquo;ll be able to submit reservation requests &amp; group/private dining inquiries
            on {{ invitation.memberFirstName }}&rsquo;s behalf, edit their profile details, upload receipts, connect
            them with friends, and message restaurants on their behalf. You will not be able to change their password
            or email or delete their account.
          </p>
          <div
            v-if="acceptState === 'accepted'"
            class="status status-success"
          >
            Accepted &mdash; you can now manage {{ invitation.memberFirstName }}&rsquo;s account from your account menu.
          </div>
          <div
            v-else-if="acceptState === 'declined'"
            class="status status-neutral"
          >
            Declined. {{ invitation.memberFirstName }} has been notified.
          </div>
          <div
            v-else-if="acceptState === 'error'"
            class="status status-error"
          >
            {{ errorMessage }}
          </div>
          <div
            v-else
            class="actions"
          >
            <VBtn
              :loading="acceptState === 'working'"
              color="primary"
              @click="accept"
            >
              Accept
            </VBtn>
            <VBtn
              :loading="acceptState === 'working'"
              variant="outlined"
              @click="decline"
            >
              Decline
            </VBtn>
          </div>
        </div>
        <div
          v-else-if="!user || !user.email"
          class="masthead-content"
        >
          <HomeMasthead
            :join-page="true"
            :verified-element="false"
            :book-btn="false"
            :learn-more-first="true"
            activate-text="Accept invitation"
            custom-headline="Admin account invitation"
            :for-clients="`from ${guestUserGroup.name}`"
            :ios-app-available="false"
            :show-restaurants-link="false"
          />
        </div>
        <div
          v-else-if="errorMessage"
          class="status status-error"
        >
          {{ errorMessage }}
        </div>
      </div>
    </section>
    <VDivider thickness="2" />
    <HomeMainContent />
  </div>
</template>

<style lang="scss" scoped>
@use "@core/scss/pages/misc.scss";

.invitation-card {
  max-width: 640px;
  margin: 2rem auto;
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.invitation-card h2 {
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.invitation-card p {
  font-size: 16px;
  line-height: 1.5;
}

.actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.status {
  margin-top: 1.25rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 16px;
}

.status-success { background: #e6f7ec; color: #1f6b3a; }
.status-neutral { background: #f1f1f1; color: #333; }
.status-error   { background: #fdecea; color: #7a1f1f; }

.masthead-content {
  padding: 0 1rem;
  margin: 1rem 0;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
